import { useState } from 'react';

import PropTypes from 'prop-types';
import { BsLock, BsPencil, BsQuestionCircle } from 'react-icons/bs';

import Button from '#globalComponents/Button/Button';
import { useError } from '#globalContexts/ErrorContext';

import { useUpdateAIConfig, useGenerateAICampaign } from '../../../../models/aiCampaigns/mutations';
import { useSingleEmailCampaign } from '../../../../models/emailCampaigns/queries';

import ProductInfoForm from './ProductInfoForm/ProductInfoForm';

/**
 * ProductInfoCard - Unified component for product info setup and display
 * @param {string} mode - 'setup' (initial form) or 'display' (view/edit mode)
 */
const ProductInfoCard = ({ campaignId, mode = 'setup' }) => {
	const [isEditing, setIsEditing] = useState(false);
	const generateMutation = useGenerateAICampaign(campaignId);
	const updateConfigMutation = useUpdateAIConfig(campaignId);
	const { data: campaign } = useSingleEmailCampaign(campaignId);
	const { showError, showSuccess } = useError();

	const config = campaign?.emailCampaignAiConfigs || {};
	const isSaving = updateConfigMutation.isPending || generateMutation.isPending;

	// Lock editing if campaign has ever been activated (status !== 'draft')
	const isLocked = campaign?.campaignStatus && campaign.campaignStatus !== 'draft';

	const initialFormData = {
		productDescription: config?.productDescription || '',
		targetAudience: config?.targetAudience || '',
		keyBenefits: config?.keyBenefits || '',
		callToAction: config?.callToAction || '',
		ctaLink: config?.ctaLink || '',
		toneOfVoiceId: config?.toneOfVoiceId || null,
		disableCreativeMode: config?.disableCreativeMode || false,
		language: config?.language || 'Deutsch',
	};

	const handleSaveProductInfo = async (formData) => {
		try {
			await updateConfigMutation.mutateAsync({
				productDescription: formData.productDescription,
				targetAudience: formData.targetAudience,
				keyBenefits: formData.keyBenefits,
				callToAction: formData.callToAction,
				ctaLink: formData.ctaLink,
				toneOfVoiceId: formData.toneOfVoiceId,
				disableCreativeMode: formData.disableCreativeMode,
				language: formData.language,
			});

			await generateMutation.mutateAsync();

			if (mode === 'display') {
				setIsEditing(false);
			}

			showSuccess({
				title: mode === 'setup' ? 'E-Mail-Generierung gestartet' : 'E-Mails werden neu generiert',
				message:
					mode === 'setup'
						? 'Die Produktinformationen wurden gespeichert und die E-Mail-Generierung gestartet.'
						: 'Die Produktinformationen wurden aktualisiert und die E-Mails werden neu generiert.',
			});
		} catch (error) {
			showError({
				title: 'Fehler beim Speichern',
				error,
			});
		}
	};

	// Setup mode: Show form directly
	if (mode === 'setup') {
		return (
			<div>
				<div className="mb-3">
					<h3 className="text-lg font-medium text-text">Beschreiben Sie Ihr Angebot</h3>
					<p className="text-sm text-bgBase-800 mt-1">
						Je detaillierter, desto besser werden die generierten E-Mails
					</p>
				</div>
				<ProductInfoForm initialData={initialFormData} onSave={handleSaveProductInfo} isSaving={isSaving} />
			</div>
		);
	}

	// Display mode: Show info with edit toggle
	return (
		<div className="bg-white border rounded-card p-4">
			{!isEditing ? (
				<div className="space-y-3">
					<div className="flex items-center justify-between">
						<h3 className="font-medium text-text">Produktinformationen</h3>
						<div className="flex items-center gap-2">
							{isLocked && (
								<div
									className="flex items-center gap-1 text-bgBase-800"
									title="Nach Aktivierung der Kampagne können die Produktinformationen nicht mehr bearbeitet werden, um die Konsistenz der bereits generierten E-Mails zu gewährleisten."
								>
									<BsQuestionCircle className="h-4 w-4 cursor-help" />
								</div>
							)}
							<Button
								onClick={() => setIsEditing(true)}
								disabled={isLocked}
								variant="outline"
								size="sm"
								className={`flex items-center gap-2 ${isLocked ? 'opacity-50 cursor-not-allowed' : ''}`}
							>
								{isLocked ? <BsLock className="h-4 w-4" /> : <BsPencil className="h-4 w-4" />}
								Bearbeiten & Neu generieren
							</Button>
						</div>
					</div>
					<div className="grid grid-cols-2 gap-4 text-sm">
						<div>
							<p className="text-bgBase-800 font-medium">Beschreibung:</p>
							<p className="text-text">{config?.productDescription || '-'}</p>
						</div>
						<div>
							<p className="text-bgBase-800 font-medium">Call-to-Action:</p>
							<p className="text-text">{config?.callToAction || '-'}</p>
						</div>
						{config?.targetAudience && (
							<div>
								<p className="text-bgBase-800 font-medium">Zielgruppe:</p>
								<p className="text-text">{config.targetAudience}</p>
							</div>
						)}
						{config?.keyBenefits && (
							<div>
								<p className="text-bgBase-800 font-medium">Vorteile:</p>
								<p className="text-text">{config.keyBenefits}</p>
							</div>
						)}
						{config?.ctaLink && (
							<div>
								<p className="text-bgBase-800 font-medium">CTA-Link:</p>
								<a
									href={config.ctaLink}
									target="_blank"
									rel="noopener noreferrer"
									className="text-primary-600 hover:underline"
								>
									{config.ctaLink}
								</a>
							</div>
						)}
						{config?.language && (
							<div>
								<p className="text-bgBase-800 font-medium">Sprache:</p>
								<p className="text-text">{config.language}</p>
							</div>
						)}
						{config?.toneOfVoices && (
							<div>
								<p className="text-bgBase-800 font-medium">Tone of Voice:</p>
								<p className="text-text">{config.toneOfVoices.name}</p>
							</div>
						)}
					</div>
				</div>
			) : (
				<div className="space-y-3">
					<div className="flex items-center justify-between">
						<h3 className="font-medium text-text">Produktinformationen bearbeiten</h3>
						<Button onClick={() => setIsEditing(false)} variant="outline" size="sm">
							Abbrechen
						</Button>
					</div>
					<ProductInfoForm initialData={initialFormData} onSave={handleSaveProductInfo} isSaving={isSaving} />
				</div>
			)}
		</div>
	);
};

ProductInfoCard.propTypes = {
	campaignId: PropTypes.string.isRequired,
	mode: PropTypes.oneOf(['setup', 'display']),
};

export default ProductInfoCard;
